import React from "react";
import { Camera } from "@/lib/icons";
import { resolvePhotoUrl } from "@/lib/api";
import { useSettings } from "@/lib/settings";

/**
 * Single source of truth for the PRONET brand mark.
 *  - Pulls the currently uploaded logo from `site_settings.logo_url` (Admin CMS)
 *  - Falls back to a solid PRONET-purple Camera icon when no logo is set
 *  - `variant`  — "badge" (rounded square, default), "plain" (no bg), "compact"
 *  - `size`     — the pixel size of the square (default 40)
 *  - `showText` — when true, renders "PRONET / PHOTOGRAPHERS NETWORK INDIA" beside the mark
 */
export default function BrandLogo({
  variant = "badge",
  size = 40,
  showText = false,
  textSize = "md",
  className = "",
  testid = "brand-logo",
}) {
  const { settings } = useSettings();
  const logoUrl = resolvePhotoUrl(settings?.logo_url);
  const brandName = settings?.website_name || "PRONET";

  const mark = (
    <span
      className={`relative inline-flex items-center justify-center overflow-hidden ${
        variant === "plain" ? "" : "rounded-lg"
      }`}
      style={{ width: size, height: size }}
    >
      {logoUrl ? (
        <img
          src={logoUrl}
          alt={brandName}
          className="w-full h-full object-contain"
        />
      ) : (
        <>
          <span className="brand-logo-fallback absolute inset-0 bg-violet-600" />
          <Camera size={Math.round(size * 0.5)} className="brand-logo-fallback-icon relative text-white" />
        </>
      )}
    </span>
  );

  if (!showText) {
    return (
      <span className={className} data-testid={testid}>
        {mark}
      </span>
    );
  }

  const nameCls = {
    sm: "text-sm",
    md: "text-lg",
    lg: "text-2xl",
    xl: "text-3xl",
  }[textSize] || "text-lg";
  const taglineCls = {
    sm: "text-[8px]",
    md: "text-[9px]",
    lg: "text-[10px]",
    xl: "text-xs",
  }[textSize] || "text-[9px]";

  return (
    <span className={`flex items-center gap-2.5 ${className}`} data-testid={testid}>
      {mark}
      <span className="leading-tight">
        <span className={`block font-display font-semibold tracking-wide text-white ${nameCls}`}>
          {brandName}
        </span>
        <span className={`block overline text-fuchsia-300/80 -mt-0.5 ${taglineCls}`}>
          Photographers Network India
        </span>
      </span>
    </span>
  );
}
